import React from 'react'
import Table from 'core-system/Table'
import { TableRowCell, TableRow } from 'core-system/Table'
import Text from 'core-system/Text'
import styled from 'styled-components'
import moment from 'moment'
import TypePill from './TypePill'
import StatusPill from './StatusPill'
import { Flex, Micromobility, QTF } from 'core-system/Icons/Sidebar'
import { Car } from 'core-system/Icons/modeTypes'
import CreditCard from 'core-system/Icons/Accounts/CreditCard'
import Coupon from 'core-system/Icons/Programs/Coupon'
import Insights from 'core-system/Icons/Sidebar/Insights'
import Tooltip from '@mui/material/Tooltip'
import wordings from '../../Shared/wordings.json'
import EditButton from '../../Shared/components/EditButton'
import SegmentsDisplay from '../../Shared/components/SegmentsDisplay'
import { ProgramsType } from '../../Shared/types'
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye'
import palette from 'core-system/Themes/palette'

const IconTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const IconsContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
  margin-left: 0.5rem;
`

const RowCell = styled(TableRowCell)`
  padding: 1.5rem 1rem !important;
`

const EditContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
`

const getIcon = (programType: ProgramsType) => {
  if (programType === 'grh') {
    return (
      <Tooltip title={wordings.grh.title}>
        <Car width={20} height={20} />
      </Tooltip>
    )
  } else if (programType === 'qtf') {
    return (
      <Tooltip title={wordings.qtf.title}>
        <QTF width={20} height={20} />
      </Tooltip>
    )
  } else if (programType === 'micromobility') {
    return (
      <Tooltip title={wordings.micromobility.title}>
        <Micromobility width={20} height={20} />
      </Tooltip>
    )
  } else if (programType === 'flex') {
    return (
      <Tooltip title={wordings.flex.title}>
        <Flex width={20} height={20} />
      </Tooltip>
    )
  } else if (programType === 'transitPass') {
    return (
      <Tooltip title={wordings.transitPass.title}>
        <CreditCard width={20} height={20} />
      </Tooltip>
    )
  } else if (programType === 'coupon') {
    return (
      <Tooltip title={wordings.coupon.title}>
        <Coupon width={20} height={20} />
      </Tooltip>
    )
  } else if (programType === 'reportedUsage') {
    return (
      <Tooltip title={wordings.reportedUsage.title}>
        <Insights width={20} height={20} />
      </Tooltip>
    )
  }
  return null
}

const nameWidth = 250
const commutersGroupWidth = 250

const ProgramRow = ({
  superProgram,
  onRowClick,
  type,
  onEditClick,
}: {
  type: string
  superProgram: any
  onRowClick: (programGroup: any) => void
  onEditClick: (programGroup: any) => void
}) => {
  const { status, selectedDate, selectedSegments, selectedPrograms, name } =
    superProgram
  return (
    <TableRow isClickable>
      <RowCell onClick={() => onRowClick(superProgram)}>
        <IconTextContainer style={{ minWidth: nameWidth }}>
          <Text variant='action3'>{name}</Text>
          <IconsContainer>
            {selectedPrograms.map((programType) =>
              getIcon(programType as ProgramsType)
            )}
          </IconsContainer>
        </IconTextContainer>
      </RowCell>
      <RowCell align='center' onClick={() => onRowClick(superProgram)}>
        <TypePill type={type as 'Program' | 'OneTime'} />
      </RowCell>
      <RowCell align='center' onClick={() => onRowClick(superProgram)}>
        <StatusPill status={status} />
      </RowCell>
      <RowCell align='center' onClick={() => onRowClick(superProgram)}>
        <Text variant='action4'>
          {moment(selectedDate.iso).format('MMM DD, YYYY')}
        </Text>
      </RowCell>
      <RowCell
        align='left'
        style={{ minWidth: commutersGroupWidth }}
        onClick={() => onRowClick(superProgram)}
      >
        <SegmentsDisplay segments={selectedSegments} displayType='table' />
      </RowCell>
      <RowCell align='center'>
        <EditContainer>
          <RemoveRedEyeIcon
            style={{ fill: palette.grey.grey700 }}
            onClick={() => onRowClick(superProgram)}
          />
          <EditButton onClick={() => onEditClick(superProgram)} />
        </EditContainer>
      </RowCell>
    </TableRow>
  )
}

interface ProgramTableProps {
  superPrograms: any[]
  onEditClick: (programGroup: any) => void
  onRowClick: (programGroup: any) => void
}

const ProgramTable = ({
  superPrograms,
  onEditClick = () => {},
  onRowClick = () => {},
}: ProgramTableProps) => {
  const HeaderCell = (text: string, align: 'center' | 'left' = 'center') => (
    <TableRowCell align={align}>
      <Text fontWeight={600} variant={'action3'}>
        {text}
      </Text>
    </TableRowCell>
  )

  const headerCells = [
    HeaderCell('Name', 'left'),
    HeaderCell('Type', 'center'),
    HeaderCell('Status', 'center'),
    HeaderCell('Launch Date', 'center'),
    HeaderCell('Commuters Group / Tag', 'left'),
    HeaderCell('Edit', 'center'),
  ]

  return (
    <Table
      headerCells={headerCells}
      rows={superPrograms.map((superProgram) => (
        <ProgramRow
          superProgram={superProgram}
          type='Program'
          onRowClick={onRowClick}
          onEditClick={onEditClick}
        />
      ))}
    />
  )
}

export default React.memo(ProgramTable)
